/* organization/OrgTimeline — org-scoped activity feed; cursor pagination; server-side filtering */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill, SearchInput } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead } from '../platform/_ui';
import { listTimeline } from '../api/org-timeline.api.js';
import { usePermissions } from './auth/usePermissions.js';
import { useOrgRuntime } from './OrganizationRuntimeContext.jsx';
import { DeniedState } from './auth/DeniedState.jsx';
import { CATS, CAT_TONE } from './constants/orgCats.js';

const CAT_ICON = {
  Organization:        'Building2',
  Members:             'Users',
  'Identity & Access': 'Lock',
  Licenses:            'Key',
  Branding:            'Eye',
  Channels:            'MessageSquare',
  Storage:             'HardDrive',
  Security:            'ShieldCheck',
  'Developer Center':  'Code',
  'AI Platform':       'Cpu',
  Settings:            'Settings',
  Workspace:           'LayoutGrid',
};

const CAT_COLOR = {
  Organization:        'var(--nx-text)',
  Members:             'var(--nx-success)',
  'Identity & Access': 'var(--nx-warning)',
  Licenses:            'var(--nx-accent)',
  Branding:            'var(--nx-accent)',
  Channels:            'var(--nx-success)',
  Storage:             'var(--nx-text-muted)',
  Security:            'var(--nx-danger)',
  'Developer Center':  'var(--nx-accent)',
  'AI Platform':       'var(--nx-accent)',
  Settings:            'var(--nx-text-soft)',
  Workspace:           'var(--nx-accent)',
};

const CAT_TINT = {
  Organization:        'var(--nx-surface-alt)',
  Members:             'var(--nx-success-tint)',
  'Identity & Access': 'var(--nx-warning-tint)',
  Licenses:            'var(--nx-accent-tint)',
  Branding:            'var(--nx-accent-tint)',
  Channels:            'var(--nx-success-tint)',
  Storage:             'var(--nx-surface-alt)',
  Security:            'var(--nx-danger-tint)',
  'Developer Center':  'var(--nx-accent-tint)',
  'AI Platform':       'var(--nx-accent-tint)',
  Settings:            'var(--nx-surface-alt)',
  Workspace:           'var(--nx-accent-tint)',
};

const relTime = (iso) => {
  if (!iso) return '—';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60_000)        return 'just now';
    if (diff < 3_600_000)     return Math.floor(diff / 60_000) + 'm ago';
    if (diff < 86_400_000)    return Math.floor(diff / 3_600_000) + 'h ago';
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return String(iso); }
};

const dayLabel = (iso) => {
  if (!iso) return 'Unknown';
  try {
    const d         = new Date(iso);
    const today     = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString())     return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return String(iso); }
};

const TimelineZeroState = ({ loading, failed, onRetry }) => {
  if (loading) return (
    <div className="nx-empty" style={{ marginTop: 40 }}>
      <NexusIcon name="Loader" size={20} /><span>Loading…</span>
    </div>
  );
  if (failed) return (
    <div className="nx-empty" style={{ marginTop: 40, flexDirection: 'column', gap: 10 }}>
      <NexusIcon name="AlertCircle" size={22} style={{ color: 'var(--nx-danger)' }} />
      <span>Failed to load activity.</span>
      <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={onRetry}>Retry</Btn>
    </div>
  );
  return (
    <div className="nx-empty" style={{ marginTop: 40 }}>
      <NexusIcon name="Activity" size={24} /><span>No activity yet.</span>
    </div>
  );
};

const Avatar = ({ name }) => {
  const str = name || '?';
  let initials;
  if (str.includes('@')) {
    const local = str.split('@')[0];
    const parts = local.split('.');
    initials = parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : local.slice(0, 2).toUpperCase();
  } else {
    initials = str.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  }
  return (
    <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--nx-accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'var(--nx-accent)', flexShrink: 0 }}>
      {initials}
    </div>
  );
};

export default function OrgTimeline({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel') || 'Back';
  const toast      = useToast();
  const perm       = usePermissions();
  const { loading: runtimeLoading } = useOrgRuntime();
  const org        = data.state.organizations.find(o => o.id === orgId);

  // All state hooks before any conditional return
  const [events, setEvents]         = useState([]);
  const [cursor, setCursor]         = useState(null);
  const [hasMore, setHasMore]       = useState(false);
  const [loading, setLoading]       = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [q, setQ]                   = useState('');
  const [cat, setCat]               = useState('All');
  const [actorFilter, setActorFilter] = useState('All');
  const [fromDate, setFromDate]     = useState('');
  const [toDate, setToDate]         = useState('');
  const debounceRef = useRef(null);

  const loadInitial = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setLoadFailed(false);
    setEvents([]);
    setCursor(null);
    setHasMore(false);
    try {
      const params = {};
      if (q)                  params.q     = q;
      if (cat !== 'All')      params.cat   = cat;
      if (actorFilter !== 'All') params.actor = actorFilter;
      if (fromDate)           params.from  = fromDate;
      if (toDate)             params.to    = toDate;
      const result = await listTimeline(orgId, params);
      const items  = result?.events ?? [];
      setEvents(items);
      setCursor(result?.nextCursor ?? null);
      setHasMore(!!result?.nextCursor);
    } catch {
      setLoadFailed(true);
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load timeline' });
    } finally { setLoading(false); }
  }, [orgId, q, cat, actorFilter, fromDate, toDate, toast]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadInitial, q ? 300 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [loadInitial]);

  // Guards after all hooks
  if (!org || runtimeLoading) return null;

  const canView = perm.has('org.timeline.view');
  if (!canView) return (
    <div className="nx-cc-page">
      <DeniedState
        reason="insufficient_permission"
        message="Activity Timeline requires Organization Admin access. Contact your org admin to request the Activity Timeline capability."
      />
    </div>
  );

  const loadMore = async () => {
    if (!cursor || loading) return;
    setLoading(true);
    try {
      const params = { cursor };
      if (q)                     params.q     = q;
      if (cat !== 'All')         params.cat   = cat;
      if (actorFilter !== 'All') params.actor = actorFilter;
      if (fromDate)              params.from  = fromDate;
      if (toDate)                params.to    = toDate;
      const result = await listTimeline(orgId, params);
      const items  = result?.events ?? [];
      setEvents(prev => [...prev, ...items]);
      setCursor(result?.nextCursor ?? null);
      setHasMore(!!result?.nextCursor);
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load more events' });
    } finally { setLoading(false); }
  };

  const members       = (data.state.members || []).filter(m => m.orgId === orgId);
  const hasFilters    = q || cat !== 'All' || actorFilter !== 'All' || fromDate || toDate;
  const clearFilters  = () => { setQ(''); setCat('All'); setActorFilter('All'); setFromDate(''); setToDate(''); };

  let body;
  if (events.length === 0) {
    body = <TimelineZeroState loading={loading} failed={loadFailed} onRetry={loadInitial} />;
  } else {
    const grouped = [];
    let lastDay   = null;
    for (const e of events) {
      const raw = e.createdAt || e.timestamp || e.time;
      const day = dayLabel(raw);
      if (day !== lastDay) { grouped.push({ kind: 'sep', label: day }); lastDay = day; }
      grouped.push({ kind: 'event', event: e });
    }
    const totalEvents = grouped.filter(g => g.kind === 'event').length;
    let evIdx = 0;
    body = (
      <div style={{ maxWidth: 640, marginTop: 8 }}>
        {grouped.map((item, gi) => {
          if (item.kind === 'sep') {
            return (
              <div key={'sep-' + gi} style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 12px' }}>
                <div style={{ height: 1, flex: 1, background: 'var(--nx-border)' }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>{item.label}</span>
                <div style={{ height: 1, flex: 1, background: 'var(--nx-border)' }} />
              </div>
            );
          }
          const a      = item.event;
          evIdx++;
          const isLast = evIdx === totalEvents;
          const evCat  = a.cat || a.category || '';
          const color  = CAT_COLOR[evCat] || 'var(--nx-text-muted)';
          const tint   = CAT_TINT[evCat]  || 'var(--nx-surface-alt)';
          const iconNm = CAT_ICON[evCat]  || 'Activity';
          const raw    = a.createdAt || a.timestamp || a.time;
          const actor  = a.who || a.actor || a.actorEmail || '';
          return (
            <div key={a.id || gi} style={{ display: 'flex', gap: 14, marginBottom: 4, position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: tint, color, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                  <NexusIcon name={iconNm} size={13} />
                </div>
                {!isLast && <div style={{ width: 2, flex: 1, minHeight: 16, background: 'var(--nx-border)', marginTop: 4 }} />}
              </div>
              <div style={{ paddingTop: 4, paddingBottom: 16, flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{a.action || a.description || '—'}</span>
                  {evCat && <Pill tone={CAT_TONE[evCat] || 'blue'}>{evCat}</Pill>}
                </div>
                {a.target && <div className="nx-muted" style={{ fontSize: 12, marginTop: 2 }}>{a.target}</div>}
                <div style={{ display: 'flex', gap: 10, marginTop: 6, alignItems: 'center' }}>
                  {actor && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Avatar name={actor} />
                      <span className="nx-muted nx-mini">{actor}</span>
                    </div>
                  )}
                  <span className="nx-muted nx-mini">{relTime(raw)}</span>
                </div>
              </div>
            </div>
          );
        })}
        {hasMore && (
          <div style={{ textAlign: 'center', marginTop: 4, paddingBottom: 16 }}>
            <Btn variant="ghost" size="sm" onClick={loadMore} disabled={loading}>
              {loading ? 'Loading…' : 'Load more'}
            </Btn>
          </div>
        )}
      </div>
    );
  }

  const evtSuffix  = events.length !== 1 ? 's' : '';
  const moreMark   = hasMore ? '+' : '';
  const timelineSub = events.length > 0
    ? `${events.length}${moreMark} event${evtSuffix} · ${org.name}`
    : `Recent activity for ${org.name}`;

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Activity Timeline"
        sub={timelineSub}
        back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchInput value={q} onChange={e => setQ(e.target.value)} placeholder="Search actions, actors, targets…" width={240} />
        <select className="nx-input" style={{ width: 170, fontSize: 13 }} value={cat} onChange={e => setCat(e.target.value)}>
          {CATS.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className="nx-input" style={{ width: 190, fontSize: 13 }} value={actorFilter} onChange={e => setActorFilter(e.target.value)}>
          <option value="All">All Actors</option>
          {members.map(m => <option key={m.email} value={m.email}>{m.name || m.email}</option>)}
        </select>
        <input type="date" className="nx-input" style={{ width: 140, fontSize: 13 }} value={fromDate} onChange={e => setFromDate(e.target.value)} title="From date" />
        <input type="date" className="nx-input" style={{ width: 140, fontSize: 13 }} value={toDate}   onChange={e => setToDate(e.target.value)}   title="To date" />
        {hasFilters && <Btn variant="ghost" size="sm" icon="X" onClick={clearFilters}>Clear</Btn>}
      </div>

      {body}
    </div>
  );
}
